import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';
import { Button } from '@spotahome/ui-library';
import { formatPrice } from '@spotahome/soyuz-currency';
import Metrics from '@spotahome/soyuz-metrics';

import AlertShape from '../shapes/alert';

import AlertWrapper from './AlertWrapper';

const SECTION_SEPARATOR = '. ';
const ITEM_SEPARATOR = ', ';

const parseDate = date =>
  date
    .split('-')
    .reverse()
    .join('/');

const capitalizeFirst = str =>
  `${str.charAt(0).toLocaleUpperCase()}${str.slice(1)}`;

const formatCurrency = (amount, isoCode) => formatPrice(amount, isoCode);
const formatText = (sep, ...slices) =>
  capitalizeFirst(slices.filter(slice => slice).join(sep));
const formatSectionText = (...slices) =>
  formatText(SECTION_SEPARATOR, ...slices);
const formatItemsText = (...slices) => formatText(ITEM_SEPARATOR, ...slices);

class Alert extends PureComponent {
  static propTypes = {
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    cityInfo: PropTypes.shape({
      name: PropTypes.string,
      currencyIsoCode: PropTypes.string
    }).isRequired,
    ...AlertShape
  };

  handleEdit = () => {
    Metrics.ga.sendEvent('My alerts', 'edit alert button', 'click button');
    this.props.onEdit(this.props.id);
  };

  handleRemove = () => {
    Metrics.ga.sendEvent('My alerts', 'delete alert button', 'click button');
    this.props.onRemove(this.props.id);
  };

  hasContent = () => {
    const { moveIn, moveOut, minPrice, maxPrice, propertyTypes } = this.props;
    return (
      moveIn ||
      moveOut ||
      minPrice ||
      maxPrice ||
      (propertyTypes && propertyTypes.length)
    );
  };

  renderPropertyTypes = () =>
    formatItemsText(
      ...this.props.propertyTypes.map(type =>
        trans(`search.filter.${type}`).toLocaleLowerCase()
      )
    );

  renderPrices = () => {
    const { minPrice, maxPrice, cityInfo } = this.props;
    const minPriceText = minPrice
      ? `${trans('alerts.alert.price.from')} ${formatCurrency(
          minPrice,
          cityInfo.currencyIsoCode
        )}`
      : '';
    const maxPriceText = maxPrice
      ? `${trans('alerts.alert.price.to')} ${formatCurrency(
          maxPrice,
          cityInfo.currencyIsoCode
        )}`
      : '';

    return formatItemsText(minPriceText, maxPriceText);
  };

  renderDates = () => {
    const { moveIn, moveOut } = this.props;
    const moveInText = moveIn
      ? `${trans('alerts.alert.date.from')} ${parseDate(moveIn)}`
      : '';
    const moveOutText = moveOut
      ? `${trans('alerts.alert.date.to')} ${parseDate(moveOut)}`
      : '';

    return formatItemsText(moveInText, moveOutText);
  };

  renderInfo = () =>
    formatSectionText(
      this.renderDates(),
      this.renderPrices(),
      this.renderPropertyTypes()
    );

  renderEmpty = () => trans('alerts.alert.empty_content');

  renderContent = () =>
    this.hasContent() ? this.renderInfo() : this.renderEmpty();

  render() {
    return (
      <AlertWrapper
        city={trans(`landingpage.label.${this.props.cityId}`)}
        content={capitalizeFirst(this.renderContent())}
        outOfDate={this.props.outOfDate}
        renderButtons={() => (
          <Fragment>
            <Button alt data-test="alert-edit-button" onClick={this.handleEdit}>
              {trans('alerts.alert.button.edit')}
            </Button>
            <Button
              alt
              data-test="alert-remove-button"
              onClick={this.handleRemove}
            >
              {trans('alerts.alert.button.delete')}
            </Button>
          </Fragment>
        )}
      />
    );
  }
}

export default Alert;
